import React from "react";
import { Container } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import PrimaryButton from "../primaryButton";
import * as userActions from "../../utils/actions/user";
import { clearPostLoginPhoneScreenSkip } from "../../utils/adminPhonePrompt";
import s from "./styles.scss";

interface Props {
  store: any;
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  capturedAt: Date | null;
}

class RootErrorBoundary extends React.Component<Props, State> {
  state: State = {
    error: null,
    capturedAt: null,
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch() {
    this.setState({ capturedAt: new Date() });
  }

  handleReload = () => {
    window.location.assign("/");
  };

  handleSignOff = () => {
    const { store } = this.props;
    const remember = store.getState().user.remember;
    clearPostLoginPhoneScreenSkip();
    store.dispatch(userActions.logoutCurrentUser(remember));
    const firebaseApi = firebase as any;
    const goHome = () => {
      window.location.href = "/";
    };
    if (typeof firebaseApi.logout === "function") {
      Promise.resolve(firebaseApi.logout())
        .then(goHome)
        .catch(goHome);
    } else {
      firebase
        .auth()
        .signOut()
        .then(goHome)
        .catch(goHome);
    }
  };

  render() {
    const { error, capturedAt } = this.state;
    const { store, children } = this.props;

    if (!error) {
      return children;
    }

    const charityRaw = store.getState()?.user?.currentCharity?.id;
    const charityDisplay =
      charityRaw && String(charityRaw).trim() !== "" ? charityRaw : "—";
    const when = (capturedAt || new Date()).toLocaleString();
    const stackText = error.stack || error.message || String(error);

    return (
      <div className={s.page}>
        <Container className={s.inner}>
          <div className={s.card}>
            <div className={s.logo} role="img" aria-label="ViewSpark" />
            <h1 className={s.title}>
              We encountered an error! Please refresh the website and try again.
            </h1>
            <p className={s.subtitle}>
              If this keeps happening, please contact us with a screenshot of this
              page.
            </p>
            <div className={s.actions}>
              <PrimaryButton type="button" onClick={this.handleReload}>
                Refresh
              </PrimaryButton>
              <PrimaryButton
                type="button"
                variant="secondary"
                onClick={this.handleSignOff}
              >
                Sign off
              </PrimaryButton>
            </div>
            <div className={s.meta}>
              <div>
                <span className={s.metaLabel}>Charity ID: </span>
                {charityDisplay}
              </div>
              <div>
                <span className={s.metaLabel}>Time: </span>
                {when}
              </div>
            </div>
            <div className={s.details}>
              <div className={s.detailsLabel}>Details</div>
              <pre className={s.pre}>{stackText}</pre>
            </div>
          </div>
        </Container>
      </div>
    );
  }
}

export default withStyles(s)(RootErrorBoundary);
